import { motion } from "framer-motion";
import WaveField from "@/components/site/WaveField";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.1, delay: 0.6 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
  }),
};

const MaskLine = ({ children, i }) => (
  <span className="block overflow-hidden">
    <motion.span variants={line} custom={i} initial="hidden" animate="show" className="block">
      {children}
    </motion.span>
  </span>
);

const META = [
  ["01", "Sonic Branding"],
  ["02", "Music Strategy"],
  ["03", "Audio Experiences"],
];

export default function Hero({ onNav }) {
  return (
    <section
      id="top"
      className="relative isolate min-h-screen w-full overflow-hidden pt-36 pb-16 px-6 lg:px-14 flex flex-col justify-center"
    >
      <div className="absolute inset-0 -z-10">
        <WaveField className="absolute inset-0" color="18,17,16" lines={52} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#FBF9F5] via-[#FBF9F5]/55 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#FBF9F5] via-transparent to-[#FBF9F5]/50" />
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 0.4 }}
        className="flex flex-wrap items-center gap-x-4 gap-y-2 font-mono-accent text-[11px] uppercase tracking-[0.4em] text-[#8C857B] mb-8 lg:mb-12"
      >
        <span>A Boutique Creative Agency Driven by Sound &amp; Music</span>
        <span className="h-1 w-1 rounded-full bg-[#C5A880]" />
        <span className="text-[#C5A880]">By Invitation &amp; Referral</span>
      </motion.div>

      <h1
        data-testid="hero-headline"
        className="font-serif-display font-light tracking-tight leading-[0.98] text-[12vw] sm:text-[9vw] lg:text-[6.6vw] text-[#121110] max-w-[18ch]"
      >
        <MaskLine i={0}>Luxury is not</MaskLine>
        <MaskLine i={1}>only seen. It is</MaskLine>
        <MaskLine i={2}>
          <span className="italic text-[#8C857B]">heard &amp; felt.</span>
        </MaskLine>
      </h1>

      <div className="mt-10 lg:mt-14 flex flex-col lg:flex-row lg:items-end gap-8 lg:gap-20">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.4, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-md text-[#5C5650] text-sm sm:text-base leading-relaxed font-light"
        >
          We craft bespoke sonic identities, music strategies and audio experiences for brands that
          value distinction, depth and cultural relevance.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.6, ease: [0.22, 1, 0.36, 1] }}
          className="flex items-center gap-6"
        >
          <button
            data-testid="hero-manifesto-button"
            onClick={() => onNav("#manifesto")}
            className="hairline-btn bg-[#121110] text-[#FBF9F5] px-7 py-4 font-mono-accent text-[10px] uppercase tracking-[0.25em] hover:bg-[#2E2823]"
          >
            Enter the Studio
          </button>
          <button
            onClick={() => onNav("#contact")}
            className="link-underline font-mono-accent text-[10px] uppercase tracking-[0.25em] text-[#121110] pb-1"
          >
            Start a Conversation
          </button>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.2, delay: 1.9 }}
        className="mt-16 lg:mt-24 grid grid-cols-3 gap-4 max-w-2xl border-t border-[#121110]/10 pt-6"
      >
        {META.map(([n, label]) => (
          <div key={n} className="flex flex-col gap-1">
            <span className="font-serif-display text-2xl text-[#C5A880]">{n}</span>
            <span className="font-mono-accent text-[9px] sm:text-[10px] uppercase tracking-[0.2em] text-[#5C5650]">
              {label}
            </span>
          </div>
        ))}
      </motion.div>
    </section>
  );
}
